import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Shield, 
  AlertTriangle, 
  ArrowLeft, 
  Home, 
  Lock,
  X
} from "lucide-react";

const PermissionDeniedModal = ({ 
  isOpen = true, 
  onClose,
  message = "You do not have permission to access this page.",
  requiredPermission = null,
  showCloseButton = true 
}) => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    if (onClose) {
      onClose();
    } else if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/dashboard");
    }
  };

  const handleGoHome = () => {
    if (onClose) {
      onClose();
    }
    navigate("/dashboard");
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-md mx-4"
        >
          {/* Modal Container */}
          <div className="relative overflow-hidden bg-white rounded-2xl shadow-2xl border border-gray-100">
            
            {/* Close Button */}
            {showCloseButton && onClose && (
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            )}

            {/* Header Section */}
            <div className="relative px-8 pt-8 pb-6 text-center bg-gradient-to-br from-red-50 to-orange-50">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-4 left-4 w-16 h-16 border-2 border-red-300 rounded-full"></div>
                <div className="absolute top-8 right-8 w-8 h-8 border-2 border-orange-300 rounded-full"></div>
                <div className="absolute bottom-4 left-8 w-12 h-12 border-2 border-red-200 rounded-full"></div>
              </div>

              {/* Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: "spring", damping: 15 }}
                className="relative mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg"
              >
                <Shield className="w-8 h-8 text-white" />
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-2xl font-bold text-gray-900 mb-2"
              >
                Access Restricted
              </motion.h2>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-sm text-gray-600"
              >
                Permission required to continue
              </motion.p>
            </div>

            {/* Content Section */}
            <div className="px-8 py-6">
              {/* Warning Icon and Message */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-start gap-3 mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800 mb-1">
                    Permission Required
                  </p>
                  <p className="text-sm text-amber-700 leading-relaxed">
                    {message}
                  </p>
                  {requiredPermission && (
                    <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-amber-100 border border-amber-300 rounded-full">
                      <Lock className="w-3 h-3 text-amber-600" />
                      <span className="text-xs font-medium text-amber-700">
                        {requiredPermission}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col gap-3"
              >
                {/* Go Back Button */}
                <button
                  onClick={handleGoBack}
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 font-semibold text-white bg-gradient-to-r from-red-500 to-orange-500 rounded-xl hover:from-red-600 hover:to-orange-600 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Go Back
                </button>

                {/* Go Home Button */}
                <button
                  onClick={handleGoHome}
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transform hover:scale-[1.02] transition-all duration-200"
                >
                  <Home className="w-4 h-4" />
                  Go to Dashboard
                </button>
              </motion.div>

              {/* Footer Note */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-6 pt-4 border-t border-gray-100 text-center"
              >
                <p className="text-xs text-gray-500">
                  Contact your administrator if you believe this is an error
                </p>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PermissionDeniedModal; 